import { ATTRIBUTION_DIMENSIONS, type AttributionDimension } from '@/services/campaigns';
import { fromDateParam, toDateParam } from '@/lib/date-param';

export interface CampaignsSort {
  by: string;
  dir: 'asc' | 'desc';
}

/** Everything the Campaigns list keeps in its URL. */
export interface CampaignsView {
  viewMode: 'flat' | 'grouped';
  /** Remembered while the flat view shows, but only written to the URL when grouped. */
  groupBy: AttributionDimension;
  search: string;
  status: string;
  planId: string;
  /** Calendar days as YYYY-MM-DD, local to the viewer. */
  dateFrom?: string;
  dateTo?: string;
  includeUntracked: boolean;
  flatSort: CampaignsSort;
  groupedSort: CampaignsSort;
  page: number;
}

const DEFAULT_GROUP_BY: AttributionDimension = 'utm_campaign';
const DEFAULT_FLAT_SORT: CampaignsSort = { by: 'createdAtUtc', dir: 'desc' };
const DEFAULT_GROUPED_SORT: CampaignsSort = { by: 'signups', dir: 'desc' };

const readDay = (value: string | null) => {
  const date = fromDateParam(value);
  return date ? toDateParam(date) : undefined;
};

// An unknown column is left to the API, which sorts by its default instead.
const readSort = (by: string | null, dir: string | null, fallback: CampaignsSort): CampaignsSort => ({
  by: by || fallback.by,
  dir: dir === 'asc' || dir === 'desc' ? dir : fallback.dir,
});

/**
 * The view a Campaigns URL describes. Anything missing or malformed reads as
 * its default, so a stale or hand-edited link still opens the page.
 */
export function readCampaignsView(params: URLSearchParams): CampaignsView {
  const groupBy = params.get('groupBy');
  const grouped = (ATTRIBUTION_DIMENSIONS as readonly string[]).includes(groupBy ?? '');
  return {
    viewMode: grouped ? 'grouped' : 'flat',
    groupBy: grouped ? (groupBy as AttributionDimension) : DEFAULT_GROUP_BY,
    search: params.get('search') ?? '',
    status: params.get('status') ?? '',
    planId: params.get('planId') ?? '',
    dateFrom: readDay(params.get('dateFrom')),
    dateTo: readDay(params.get('dateTo')),
    includeUntracked: params.get('untracked') === '1',
    flatSort: readSort(params.get('sort'), params.get('dir'), DEFAULT_FLAT_SORT),
    groupedSort: readSort(params.get('groupSort'), params.get('groupDir'), DEFAULT_GROUPED_SORT),
    page: Math.max(1, parseInt(params.get('page') || '1', 10) || 1),
  };
}

/** The URL for a view, with every default left out. */
export function writeCampaignsView(view: CampaignsView): URLSearchParams {
  const params = new URLSearchParams();
  if (view.search) params.set('search', view.search);
  if (view.status) params.set('status', view.status);
  if (view.planId) params.set('planId', view.planId);
  if (view.dateFrom) params.set('dateFrom', view.dateFrom);
  if (view.dateTo) params.set('dateTo', view.dateTo);
  if (view.includeUntracked) params.set('untracked', '1');
  if (view.viewMode === 'grouped') params.set('groupBy', view.groupBy);
  if (view.flatSort.by !== DEFAULT_FLAT_SORT.by || view.flatSort.dir !== DEFAULT_FLAT_SORT.dir) {
    params.set('sort', view.flatSort.by);
    params.set('dir', view.flatSort.dir);
  }
  if (view.groupedSort.by !== DEFAULT_GROUPED_SORT.by || view.groupedSort.dir !== DEFAULT_GROUPED_SORT.dir) {
    params.set('groupSort', view.groupedSort.by);
    params.set('groupDir', view.groupedSort.dir);
  }
  if (view.page > 1) params.set('page', String(view.page));
  return params;
}

/**
 * The agency page's query when Campaigns opens it: the Campaigns view as it
 * stands, marked from=campaigns, so the page's back arrow can return to it.
 */
export function agencySearchFromCampaigns(campaignsParams: URLSearchParams): string {
  return new URLSearchParams([['from', 'campaigns'], ...campaignsParams]).toString();
}

/**
 * Where the agency page's back arrow goes: the list that opened the agency,
 * with that list's own query. The Agencies list hands its query down as is;
 * Campaigns marks its own with from=campaigns.
 */
export function listPathFor(agencyParams: URLSearchParams): string {
  const params = new URLSearchParams(agencyParams);
  const list = params.get('from') === 'campaigns' ? '/campaigns' : '/agencies';
  params.delete('from');
  const query = params.toString();
  return query ? `${list}?${query}` : list;
}
